import type { TimelineFilters, TimelineSortOptions } from "../components/types/timeline-filters";
import type { Tables } from "@/lib/supabase/database.types";

export type { TimelineFilters, TimelineSortOptions };

/**
 * Free-form metadata attached to a timeline item. Known display fields are
 * typed; everything else is preserved via the `unknown`-valued index signature
 * (read it through a narrowing check, never assume a shape).
 */
export interface TimelineItemMetadata {
  provider?: string;
  connectionId?: string;
  threadId?: string;
  thread_id?: string;
  webLink?: string;
  update_type?: string;
  user_name?: string;
  user_email?: string;
  [key: string]: unknown;
}

export interface TimelineItem {
  id: string;
  type: "communication" | "ticket_update";
  title: string;
  /**
   * List/teaser body. After S3 offload this is usually `body_preview` (~280 chars).
   * Full HTML is loaded in the detail modal via `body_s3_key` + the body API.
   */
  body: string;
  /** Present when the full body lives in S3 (UC-COMMS-BODY). */
  body_s3_key?: string | null;
  occurred_at: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  contact_id: string;
  /**
   * Whose activity this is, from the perspective of the timeline being viewed.
   *
   * - `"contact"` (default) — this person's own communication.
   * - `"company"` — INHERITED company-level activity: a call/SMS/email on a
   *   shared line or mailbox that the company owns (front desk, info@). It is
   *   stored once against the company's line-contact anchor
   *   (`companies.line_contact_id`) and shown on every colleague's timeline,
   *   rather than copied onto each person's row — which is what used to create
   *   duplicate "people" who were really one switchboard. See
   *   docs/engineering-rules/CONTACT_METHOD_EXCLUSIVITY.md.
   */
  scope?: "contact" | "company";
  /** For inherited items: the company whose shared line produced this. */
  inherited_from?: {
    companyId: string;
    companyName: string | null;
    /** The shared number/address as held by the company, for the badge tooltip. */
    methodLabel?: string | null;
  };
  direction?: "inbound" | "outbound";
  status?: string;
  metadata?: TimelineItemMetadata;
  communication_type?: {
    key: string;
    label: string;
  };
  amount?: number;
  currency?: string;
  file_url?: string;
  file_name?: string;
  priority?: string;
  due_date?: string;
  assignee?: {
    id: string;
    name: string;
  };
  provider?: string;
  connection_id?: string;
  external_id?: string;
  sender?: {
    email: string;
    name?: string;
  };
  recipients?: Array<{
    email: string;
    name?: string;
  }>;
  /**
   * How many files this message carries — `communications.attachment_count`,
   * read straight from the row. This is the authoritative paperclip signal: the
   * manifest below can be absent (a row written before manifests, or one whose
   * stored value failed validation) while the count is still known.
   */
  attachment_count?: number;
  /**
   * The files themselves, when the row carries a manifest.
   *
   * `url` and `size` are OPTIONAL because a stored manifest has neither
   * guaranteed: the bytes live behind a provider/S3 key that is resolved
   * server-side, and older producers wrote no size. A list row names the files;
   * it does not link to them.
   */
  attachments?: Array<{
    name: string;
    url?: string;
    size?: number;
    contentType?: string;
  }>;
  /**
   * Email open-tracking state for this communication. Present only when the sent
   * email opted into tracking (a token was minted). `openedAt` is the first
   * recorded open; absent `open_tracking` means the email was not tracked.
   */
  open_tracking?: {
    tracked: boolean;
    openedAt: string | null;
    lastOpenedAt: string | null;
    openCount: number;
  };
}

export interface UseEnhancedTimelineOptions {
  filters?: TimelineFilters;
  sort?: TimelineSortOptions;
  limit?: number;
  enabled?: boolean;
  ticketId?: string;
  contactId?: string;
  /**
   * Omit inherited company-line activity (`scope: "company"`), showing only the
   * person's own communications. Default false — company-level activity is
   * inherited so a shared front-desk call is visible to everyone it concerns
   * without being duplicated onto their rows.
   */
  excludeCompanyActivity?: boolean;
}

/** Codegen-derived: the app_users columns the ticket-updates join selects. */
export type TicketUpdateAppUserRow = Pick<
  Tables<"app_users">,
  "id" | "email" | "first_name" | "last_name"
>;

/**
 * Codegen-derived: the ticket_updates columns the timeline selects, plus the
 * embedded app_users author (PostgREST returns an object or array for the join).
 */
export type TicketUpdateQueryRow = Pick<
  Tables<"ticket_updates">,
  "id" | "ticket_id" | "user_id" | "update_type" | "content" | "is_internal" | "created_at"
> & {
  app_users: TicketUpdateAppUserRow | TicketUpdateAppUserRow[] | null;
};

export function ticketUpdateAuthor(row: TicketUpdateQueryRow): TicketUpdateAppUserRow | null {
  const u = row.app_users;
  if (!u) return null;
  return Array.isArray(u) ? (u[0] ?? null) : u;
}
